import { useCallback } from 'react';
import { Dropdown, Avatar } from 'antd';
import LanguageDropdown from './LanguageDropdown';
import {
	UserOutlined,
	AppstoreAddOutlined,
	DownOutlined,
	SwapOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import HeaderLogo from '../../assets/images/header-logo.svg';
import LogoutImage from '../../assets/images/logout-red-icon.svg';
import { useAuth } from '../../hooks/useAuth';
import { useLocation } from 'react-router-dom';
import { EXAM_MODULE_HOME, ROOT, SELECT_MODULE } from '../../shared/Routes';
import { useBatchId } from '../../hooks/useBatchId';
import { UserRole } from '../../shared/VolunteerApplicationServiceTypes';
import { NPS_PATH, PREWORK_PATH } from '../../shared/Constants';
import useUserSession from '../../hooks/useUserSession';
import { ProfileMenu, ProfileMenuItem } from './ProfileMenu';

interface NewAppHeaderProps {
	logout?: () => void | Promise<void>;
}

const LogoutIcon = () => (
	<>
		<img src={LogoutImage} alt="Logout Icon" />
	</>
);

export const NewAppHeader = ({ logout }: NewAppHeaderProps) => {
	const { t } = useTranslation();
	const logoutText = t('logout');
	const selectModule = t('selectModule');
	const switchRole = t('switchRole');
	const { mySrcmProfile } = useAuth();
	const { selectedRole, allRolesWithModules } = useUserSession();
	const { navigateToRoute } = useBatchId();
	const location = useLocation();
	const disableSNPClick = selectedRole === UserRole.APPLICANT;
	let module;
	if (location.pathname.includes(NPS_PATH)) {
		module = 'New Preceptor Support';
	} else if (location.pathname.includes(PREWORK_PATH)) {
		module = 'Prework';
	} else if (location.pathname.includes(EXAM_MODULE_HOME)) {
		module = 'Exam Module';
	}

	const showSwitchRole =
		allRolesWithModules &&
		allRolesWithModules.length > 1 &&
		selectedRole !== UserRole.APPLICANT;

	const handleSwitchRoleClick = useCallback(() => {
		navigateToRoute(ROOT);
	}, [navigateToRoute]);

	const handleSNPClick = useCallback(() => {
		navigateToRoute(SELECT_MODULE);
	}, [navigateToRoute]);

	const handleLogoutClick = useCallback(() => {
		if (logout) {
			void logout();
		}
	}, [logout]);

	const profileMenuItems: ProfileMenuItem[] = [
		{
			key: 'switch-role',
			label: switchRole,
			icon: <SwapOutlined />,
			onClick: handleSwitchRoleClick,
			visible: showSwitchRole || false,
		},
		{
			key: 'snp',
			label: selectModule,
			icon: <AppstoreAddOutlined />,
			onClick: handleSNPClick,
			visible: selectedRole !== UserRole.APPLICANT,
		},
		{
			key: 'logout',
			label: logoutText,
			icon: <LogoutIcon />,
			onClick: handleLogoutClick,
			variant: 'danger',
		},
	];

	return (
		<div className="flex justify-between items-center p-[8px] bg-nav-blue text-white px-6">
			<div className="flex items-center whitespace-nowrap">
				<img src={HeaderLogo} alt="Header Logo" className="hidden md:block" />
				<div className="h-full w-px bg-white mr-4 hidden md:block">&nbsp;</div>
				<span
					className={`text-xl font-bold ${
						!disableSNPClick ? 'cursor-pointer hover:underline' : ''
					}`}
					onClick={!disableSNPClick ? handleSNPClick : () => {}}
				>
					SNP
				</span>
				{module && (
					<span className="text-xl font-bold hidden md:block">
						&nbsp;{`- ${module}`}
					</span>
				)}
			</div>
			<div className="flex items-center gap-4">
				<div>
					<LanguageDropdown className="w-28 text-center" />
				</div>
				<Dropdown
					dropdownRender={() => <ProfileMenu items={profileMenuItems} />}
					trigger={['click']}
					placement="bottomRight"
				>
					<div className="flex items-center gap-x-1 cursor-pointer select-none">
						<Avatar
							src={<UserOutlined />}
							size={36}
							className="border border-gray-300"
						/>
						<span className="font-sans font-medium text-gray-700 text-sm">
							{mySrcmProfile?.name}
						</span>
						<DownOutlined className="text-white text-xs pl-1" />
					</div>
				</Dropdown>
			</div>
		</div>
	);
};
